import * as React from "react";
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    Stack,
    Tooltip,
    IconButton,
} from "@mui/material";
import { Psychology, Phone } from "@mui/icons-material";
import { getSession } from "../../../auth";
import { useTelephony } from "../../../contexts/TelephonyContext";
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';

const HARD_LIMIT = 100; // limite máximo de registros



function getStatusText(it) {
    if (it.answered === true) return "Atendida";
    if (it.ended === true && it.answered !== true) return "Não atendida";
    return "-";
}

function formatDuration(sec) {
    const n = Number(sec);
    if (!n || Number.isNaN(n)) return "";
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const s = n % 60;
    if (h > 0) {
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
            s
        ).padStart(2, "0")}`;
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function toDateFromTimestamp(ts) {
    if (!ts) return new Date();
    const n = Number(ts);
    if (!Number.isNaN(n)) {
        if (String(ts).length <= 10) {
            return new Date(n * 1000);
        }
        return new Date(n);
    }
    return new Date();
}

function reviveHistoryItem(raw) {
    if (!raw) return raw;
    return {
        ...raw,
        data: raw.data ? new Date(raw.data) : null,
        startedAt: raw.startedAt ? new Date(raw.startedAt) : null,
        connectedAt: raw.connectedAt ? new Date(raw.connectedAt) : null,
        endedAt: raw.endedAt ? new Date(raw.endedAt) : null,
    };
}

export default function SidebarCallHistory({
    embedded = true,
    height,
    title = "Histórico",    
    maxItems = HARD_LIMIT,
    onItemClick = null,
    onMotivationClick = null,
    setDialNumberFromHistory,
}) {
    const {
        initiateCall,
        callStatus,        
      } = useTelephony();
    const session = getSession();    
    const userId = session?.profile?.id || "anon";
    const storageKey = `telephony:callHistory:${userId}`;

    const [items, setItems] = React.useState([]);

    const refreshFromStorage = React.useCallback(() => {
        try {
            const raw = localStorage.getItem(storageKey);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            const revived = Array.isArray(parsed) ? parsed.map(reviveHistoryItem) : [];
            setItems(revived.slice(0, HARD_LIMIT));
        } catch (err) {
            console.warn("SidebarCallHistory: refresh error:", err);
        }
    }, [storageKey]);   


    // load
    React.useEffect(() => {
        refreshFromStorage();
    }, [refreshFromStorage]);

    React.useEffect(() => {
        const onRefresh = () => refreshFromStorage();
        window.addEventListener("callhistory:refresh", onRefresh);
        return () => window.removeEventListener("callhistory:refresh", onRefresh);
    }, [refreshFromStorage]);


    // sincroniza entre abas quando o WebphoneMenu gravar no storage
    React.useEffect(() => {
        const onStorage = (e) => {
            if (e.key === storageKey && e.newValue) {
                try {
                    const parsed = JSON.parse(e.newValue);
                    const revived = Array.isArray(parsed) ? parsed.map(reviveHistoryItem) : [];
                    setItems(revived.slice(0, HARD_LIMIT));
                } catch (err) {
                    console.warn("SidebarCallHistory: storage parse error:", err);
                }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, [storageKey]);

    // ao voltar a aba, recarrega do storage
    React.useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            try {
                const raw = localStorage.getItem(storageKey);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    const revived = Array.isArray(parsed) ? parsed.map(reviveHistoryItem) : [];
                    setItems(revived.slice(0, HARD_LIMIT));
                }
            } catch { }
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [storageKey]);    

    const paddY = 1;

    return (
        <Paper
            variant="outlined"
            square
            sx={{
                position: "relative",
                width: "100%",
                height: height || "100%",
                maxHeight: "100%",
                display: "flex",
                flexDirection: "column",
                borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    textAlign: "center",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    bgcolor: "background.paper",
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
                <List disablePadding>
                    {items.map((it, idx) => (
                        <React.Fragment key={it.id}>
                            <ListItem 
                                sx={{ 
                                    py: paddY, 
                                    px: 2, 
                                    alignItems: "flex-start",
                                    cursor: onItemClick ? "pointer" : "default",
                                    "&:hover": onItemClick ? {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                    } : {},
                                }}
                                onClick={() => {
                                    if (onItemClick) {
                                        onItemClick(it);
                                    }
                                }}
                                secondaryAction={
                                    <Stack direction="row" spacing={0.5}>
                                        {it.phoneNumber && (
                                            <Tooltip title="Ligação">
                                                <span>
                                                    <IconButton
                                                        edge="end"
                                                        size="small"
                                                        sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                                                        disabled={callStatus.status === "connected"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const numberToDial = it.phoneNumber;
                                                            if (setDialNumberFromHistory) {
                                                                setDialNumberFromHistory(numberToDial);
                                                            }
                                                            initiateCall(numberToDial);
                                                        }}
                                                    >
                                                        <Phone fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        {onMotivationClick && (
                                            <Tooltip title="Motivar chamada">
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onMotivationClick(it);
                                                    }}
                                                >
                                                    <Psychology fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}                                        
                                    </Stack>
                                }
                            >
                                <ListItemText
                                    primary={
                                        <Stack spacing={0.25}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {it.translatedDirection} {it.agent ? `(${it.agent})` : ""} • {getStatusText(it)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {it.contactName ? it.contactName : "—"}{" "}
                                                {it.phoneNumber ? `(${it.phoneNumber})` : ""}
                                            </Typography>
                                            {it.contactEnterpriseName ? (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Tooltip title="Empresa" arrow>
                                                        <MapsHomeWorkOutlinedIcon 
                                                            sx={{ 
                                                                fontSize: 18, 
                                                                color: "text.secondary" 
                                                            }} />
                                                    </Tooltip>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {it.contactEnterpriseName}
                                                    </Typography>
                                                </Stack>) : null}
                                            <Typography variant="body2" color="text.secondary">
                                                {it.data
                                                    ? `${String(it.data.getDate()).padStart(
                                                        2,
                                                        "0"
                                                    )}/${String(it.data.getMonth() + 1).padStart(
                                                        2,
                                                        "0"
                                                    )} ${String(it.data.getHours()).padStart(
                                                        2,
                                                        "0"
                                                    )}:${String(it.data.getMinutes()).padStart(
                                                        2,
                                                        "0"
                                                    )}:${String(it.data.getSeconds()).padStart(
                                                        2,
                                                        "0"
                                                    )}`
                                                    : ""}
                                                {it.billsec
                                                    ? ` (${formatDuration(it.billsec)})`
                                                    : ""}
                                            </Typography>
                                            {it.motivo ? (
                                                <Typography variant="body2" color="text.secondary">
                                                    Motivo: {it.motivo}
                                                </Typography>
                                            ) : null}
                                        </Stack>
                                    }
                                />
                            </ListItem>
                            {idx < items.length - 1 && <Divider sx={{ mx: 2 }} />}
                        </React.Fragment>
                    ))}
                </List>
            </Box>
        </Paper>
    );
}
